/**
 * Formats a report date string (YYYY-MM) into a readable month and year format
 * @param reportDate Report date in YYYY-MM format
 * @returns Formatted date string (e.g., "April 2025")
 */
export const formatReportDate = (reportDate: string): string => {
  if (!reportDate || !reportDate.match(/^\d{4}-\d{2}$/)) {
    return ''
  }

  const [year, month] = reportDate.split('-')
  const monthIndex = parseInt(month, 10) - 1 // Convert to 0-based index

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  return `${monthNames[monthIndex]} ${year}`
}

/**
 * Calculates how many months old a report is compared to the current date
 * @param reportDate Report date in YYYY-MM format
 * @returns Number of months between the report date and current date
 */
export const getReportAgeInMonths = (reportDate: string): number => {
  if (!reportDate || !reportDate.match(/^\d{4}-\d{2}$/)) {
    return Infinity // Return a very large number for reports that don't exist
  }

  const [reportYear, reportMonth] = reportDate
    .split('-')
    .map((num) => parseInt(num, 10))
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth() + 1 // JavaScript months are 0-indexed

  // Calculate the difference in months
  return (currentYear - reportYear) * 12 + (currentMonth - reportMonth)
}

/**
 * Determines the appropriate color scheme based on report age
 * @param reportAge Age of the report in months
 * @returns Color scheme name for the UI component
 */
export const getColorSchemeByAge = (reportAge: number): string => {
  if (reportAge === 0) {
    return 'green'
  } else if (reportAge === 1) {
    return 'blue'
  } else if (reportAge >= 2 && reportAge <= 3) {
    return 'orange'
  } else {
    return 'red'
  }
}

/**
 * Gets the current month and year display string
 * @returns Current month and year formatted string (e.g., "April 2025")
 */
export const getCurrentMonthYearDisplay = (): string => {
  const now = new Date()
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]
  const currentMonthName = monthNames[now.getMonth()]
  const currentYear = now.getFullYear()

  return `${currentMonthName} ${currentYear}`
}

// Constants for sort delays
export const SORT_DELAYS = {
  SPINNER_VISIBILITY: 300,
  SORTING_OPERATION: 300,
}

// Constants for report age thresholds
export const REPORT_AGE_THRESHOLDS = {
  CURRENT: 0,
  RECENT: 1,
  MEDIUM: 3,
}
